import { useCallback } from 'react';
import sortBy from 'lodash/sortBy';

import { DIRECTION_LEFT } from 'webclient.constants';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import {
  Sortable,
  SortableContent,
  SortableHeader,
  SortableItem,
} from 'approot/shared/sortable/sortable';
import { Box } from 'approot/shared/design-system/box/box.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import customNanoId from 'lib/nanoid/nanoid';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import Switch from 'approot/shared/switch/switch';
import {
  EditProps,
  MAX_TEXT_LABELS,
  RiveAnimationProperties,
  TextRun,
} from '../rive.constants';
import { PStyled } from 'approot/shared/styles/typography.style';

const RiveSettingsTextLabels = ({
  properties,
  onUpdate,
}: EditProps & {
  onUpdate: (properties: RiveAnimationProperties) => void;
}) => {
  const sortedTextRuns = sortBy(properties.rive?.textLabels || [], 'sort');

  const updateTextLabels = useCallback(
    (textLabels: TextRun[]) => {
      onUpdate({
        ...properties,
        rive: {
          ...properties.rive,
          textLabels,
        },
      });
    },
    [properties, onUpdate]
  );

  const handleAdd = useCallback(
    (sortedFields: TextRun[]) => {
      updateTextLabels([
        ...sortedFields,
        {
          __typename: 'RiveTextRun',
          itemId: customNanoId(),
          text: '',
          sort: getReorderSort(
            sortedFields,
            sortedFields.length,
            DIRECTION_LEFT
          ),
        },
      ]);
    },
    [updateTextLabels]
  );

  const handleUpdate = useCallback(
    (item: TextRun, sortedFields: TextRun[]) => {
      updateTextLabels(
        sortedFields.map(s => (s.itemId === item.itemId ? item : s))
      );
    },
    [updateTextLabels]
  );

  const handleDelete = useCallback(
    (itemId: string, sortedFields: TextRun[]) => {
      updateTextLabels(sortedFields.filter(s => s.itemId !== itemId));
    },
    [updateTextLabels]
  );

  return (
    <>
      <SettingsGroup>
        <SettingsLabel>Enable Labels</SettingsLabel>
        <Switch
          ariaLabel="Enable Labels"
          state={!!properties.rive?.enableTextLabels}
          onClick={() =>
            onUpdate({
              ...properties,
              rive: {
                ...properties.rive,
                enableTextLabels: !properties.rive?.enableTextLabels,
              },
            })
          }
        />
      </SettingsGroup>

      {properties.rive?.enableTextLabels && (
        <>
          <Sortable<TextRun>
            key={sortedTextRuns.map(s => s.itemId).join(',')}
            list={sortedTextRuns}
            renderItem={(item, index) => {
              const dataItem = properties?.rive?.textLabels?.find(
                s => s.itemId === item.itemId
              );
              return (
                <SortableItem key={item.itemId}>
                  <SortableHeader>
                    <DeleteButtonIcon
                      $isSmall
                      aria-label={`Delete option ${index + 1}`}
                      data-selector="icon-delete"
                      onClick={() => handleDelete(item.itemId, sortedTextRuns)}
                    />
                  </SortableHeader>
                  <SortableContent>
                    <PStyled marginBottom={4}>Label {index + 1}</PStyled>
                    <AutoResizingTextArea
                      data-selector="rive-label-text-input"
                      showCounter
                      maxLength={1000}
                      id={`field-${index}`}
                      name={`field-${index}`}
                      initialValue={dataItem?.text}
                      onChange={e =>
                        handleUpdate(
                          { ...item, text: e.target.value },
                          sortedTextRuns
                        )
                      }
                    />
                  </SortableContent>
                </SortableItem>
              );
            }}
            onSortChange={({ sourceIndex, destinationIndex, direction }) => {
              const field = sortedTextRuns[sourceIndex];
              if (sourceIndex !== destinationIndex) {
                const newSort = getReorderSort<TextRun>(
                  sortedTextRuns,
                  destinationIndex,
                  direction
                );
                handleUpdate({ ...field, sort: newSort }, sortedTextRuns);
              }
            }}
          />
          <Box $flexDirection="column" $marginBottom={24}>
            <SecondaryButton
              type="button"
              disabled={sortedTextRuns.length >= MAX_TEXT_LABELS}
              onClick={() => handleAdd(sortedTextRuns)}
              data-selector="add-label"
            >
              Add Label
            </SecondaryButton>
          </Box>
        </>
      )}
    </>
  );
};

export default RiveSettingsTextLabels;
